import React from "react";
import { useDispatch, useSelector } from "react-redux";
import * as Yup from "yup";

import { createNewProject } from "./ProjectActions";
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Stack, MenuItem, Typography } from "@mui/material";
import { Formik, Form, Field } from "formik";
import { Select, TextField } from "formik-mui";
import { global } from "../../context/global";
import { Capitalize } from "../../functions/Formatters";
import { Link, Navigate } from "react-router-dom";

export default function NewProjectDialog({ open, onClose }) {
    const dispatch = useDispatch();
    const organisations = useSelector((state) => state.organisations.organisations).filter((org) => org.user_is_admin);

    const singleOrg = organisations.length === 1;
    const defaultOrg = singleOrg ? organisations[0].id : "";

    const createProjectAndCloseDialog = (data) => {
        createNewProject(data, dispatch, onClose);
    };

    const userOptions = (orgId) => {
        let org = organisations.find((org) => org.id == orgId);
        let members = org?.members || [];
        members = [...members].sort((a, b) => a.name.localeCompare(b.name));
        return members;
    };

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby={"new_project_dialog"}
            maxWidth="md"
        >
            <DialogTitle id={"new_project_dialog"}>Nieuw project</DialogTitle>
            <Formik
                initialValues={{
                    name: "",
                    description: "",
                    organisation: defaultOrg,
                    owners: [],
                    collaborators: [],
                    readers: [],
                }}
                validationSchema={Yup.object({
                    name: Yup.string()
                        .max(255, "Naam mag niet meer dan 255 tekens zijn")
                        .trim()
                        .required("Naam is vereist"),
                    description: Yup.string().trim(),
                    organisation: Yup.number().required(
                        `${global.organisation} is vereist`
                    ),
                    owners: Yup.array()
                        .required()
                        .min(1, "Minimaal 1 eigenaar"),
                    collaborators: Yup.array(),
                    readers: Yup.array(),
                })}
                onSubmit={(values) => {
                    createProjectAndCloseDialog(values);
                }}
            >
                {({ isValid, values }) => (
                    <Form>
                        <DialogContent>
                            <Stack spacing={2}>
                                <Typography>
                                    Hier kunt u de algemene projectinformatie
                                    invullen, zoals de projectnaam, een
                                    beschrijving en de betrokken medewerkers
                                    toevoegen.
                                    </Typography>
                                    <ul>
                                        <li>
                                            <b>Eigenaren: </b>medewerkers die
                                            volledige rechten hebben. Zij kunnen
                                            alle data en informatie aan het
                                            project toevoegen, bewerken en
                                            verwijderen.
                                        </li>
                                        <li>
                                            <b>Bijdragers: </b>medewerkers die
                                            informatie kunnen toevoegen, maar
                                            geen ontwerpen mogen aanmaken of
                                            verwijderen.{" "}
                                        </li>
                                        <li>
                                            <b>Lezers: </b>medewerkers die het
                                            project kunnen inzien, maar geen
                                            wijzigingen kunnen aanbrengen.
                                        </li>
                                    </ul>
                                    <Typography>
                                    Let op: de medewerkers dienen wel al een
                                    account te hebben voor het dashboard om ze
                                    toe te kunnen voegen. Dit gaat via{" "}
                                    <Link to="/admin/users">
                                        registreren.
                                    </Link>
                                </Typography>

                                <Field
                                    component={TextField}
                                    name="name"
                                    label="Naam"
                                    type="text"
                                    placeholder="Naam"
                                />
                                <Field
                                    component={TextField}
                                    name="description"
                                    label="Beschrijving"
                                    multiline
                                    minRows={4}
                                    maxRows={8}
                                    inputProps={{
                                        autoComplete: "new-password",
                                    }}
                                />
                                {organisations.length > 1 && (
                                    <Field
                                        component={Select}
                                        name="organisation"
                                        type="select"
                                        disabled={organisations.length === 1}
                                        label={Capitalize(global.organisation)}
                                        hidden={singleOrg}
                                    >
                                        {organisations.map((option) => (
                                            <MenuItem
                                                key={option.id}
                                                value={option.id}
                                            >
                                                {option.name}
                                            </MenuItem>
                                        ))}
                                    </Field>
                                )}
                                <Field
                                    component={Select}
                                    name="owners"
                                    type="select"
                                    multiple
                                    disabled={values.organisation === ""}
                                    label="Eigenaren"
                                    onClose={() => {}}
                                    autoWidth={true}
                                >
                                    {userOptions(values.organisation).map(
                                        (option) => (
                                            <MenuItem
                                                key={option.id}
                                                value={option.id}
                                            >
                                                {option.name}
                                            </MenuItem>
                                        )
                                    )}
                                </Field>
                                <Field
                                    component={Select}
                                    name="collaborators"
                                    type="select"
                                    multiple
                                    disabled={values.organisation === ""}
                                    label="Bijdragers"
                                    onClose={() => {}}
                                    autoWidth={true}
                                >
                                    {userOptions(values.organisation).map(
                                        (option) => (
                                            <MenuItem
                                                key={option.id}
                                                value={option.id}
                                            >
                                                {option.name}
                                            </MenuItem>
                                        )
                                    )}
                                </Field>
                                <Field
                                    component={Select}
                                    name="readers"
                                    type="select"
                                    multiple
                                    placeholder="Lezers"
                                    disabled={values.organisation === ""}
                                    label="Lezers"
                                    autoWidth={true}
                                    onClose={() => {}}
                                >
                                    {userOptions(values.organisation).map(
                                        (option) => (
                                            <MenuItem
                                                key={option.id}
                                                value={option.id}
                                            >
                                                {option.name}
                                            </MenuItem>
                                        )
                                    )}
                                </Field>
                            </Stack>
                        </DialogContent>
                        <DialogActions>
                            <Button
                                type="submit"
                                color="primary"
                                variant="contained"
                                disabled={!isValid}
                            >
                                Project aanmaken
                            </Button>
                            <Button onClick={() => onClose()} color="primary">
                                Annuleren
                            </Button>
                        </DialogActions>
                    </Form>
                )}
            </Formik>
        </Dialog>
    );
}
